import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Helmet } from "react-helmet-async";

/**
 * Breadcrumb — auto-hides on Home, renders trail + BreadcrumbList schema.
 *
 * Props:
 *  - items: [{ label, to? }]  — last item is the current page (no `to`)
 *
 * If `items` is not supplied, the component renders nothing (safe fallback).
 * Callers can supply items derived from the route/page's data.
 */
const Breadcrumb = ({ items }) => {
  const { pathname } = useLocation();

  if (!items || items.length === 0 || pathname === "/") return null;

  // Always prepend Home for consistent hierarchy.
  const trail = [{ label: "Home", to: "/" }, ...items];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.label,
      ...(c.to ? { item: `https://www.lutrom.co.uk${c.to}` } : {}),
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <nav
        aria-label="Breadcrumb"
        className="border-b"
        style={{ borderColor: "var(--lt-border)", background: "rgba(6,11,24,0.55)" }}
        data-testid="breadcrumb"
      >
        <ol className="max-w-7xl mx-auto px-5 lg:px-10 h-11 flex items-center gap-1.5 text-[13px] overflow-x-auto whitespace-nowrap">
          {trail.map((c, i) => {
            const isLast = i === trail.length - 1;
            return (
              <li key={`${c.label}-${i}`} className="flex items-center gap-1.5">
                {i > 0 && (
                  <ChevronRight
                    size={13}
                    className="text-[color:var(--lt-text-mute)] flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
                {isLast || !c.to ? (
                  <span
                    className="text-white font-medium"
                    aria-current={isLast ? "page" : undefined}
                    data-testid={`breadcrumb-current`}
                  >
                    {c.label}
                  </span>
                ) : (
                  <Link
                    to={c.to}
                    className="text-[color:var(--lt-text-dim)] hover:text-white transition-colors"
                    data-testid={`breadcrumb-link-${i}`}
                  >
                    {c.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumb;
